import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { ToastProvider } from "@fiskhub/ui";
import { ApiRequestError } from "@/api/client";
import { router } from "@/router";

import "@fiskhub/ui/styles.css";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * Serve the cache instantly, then go and check.
       *
       * `staleTime` alone decided both questions, and answered the second one
       * wrong: within 30 seconds of the last fetch a screen would mount, read
       * the cache, and issue no request at all — so returning to Invoices after
       * fiscalizing showed the list as it was, and the only way to see the new
       * receipt was a hard refresh.
       *
       * `refetchOnMount: "always"` splits the two apart. Mounting a screen is
       * an explicit act by a person who wants to see that data now, so it
       * always refetches; `staleTime` goes on governing the background
       * refetches that nobody asked for. The cached rows still paint
       * immediately, so a revisit shows content rather than a skeleton and
       * updates underneath.
       *
       * `refetchOnWindowFocus` stays off deliberately: alt-tabbing back is not
       * a request for fresh data, and a refetch landing under a half-filled
       * form is a worse outcome than a stale number.
       */
      staleTime: 30_000,
      refetchOnMount: "always",
      refetchOnWindowFocus: false,
      // A 4xx is our bug or the caller's; only network and 5xx are worth retrying.
      retry: (failureCount, error) =>
        error instanceof ApiRequestError && error.retryable && failureCount < 2,
      // The default ("online") PAUSES a query when the browser reports offline,
      // which leaves a loading skeleton on screen indefinitely — a failed fetch
      // then looks exactly like a slow one. Always attempt, and let the request
      // fail loudly into an error state instead.
      networkMode: "always",
    },
    mutations: { retry: false, networkMode: "always" },
  },
});

async function start() {
  const container = document.getElementById("root");
  if (!container) throw new Error('No #root element — check index.html');

  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void start();
